import axios from 'axios'
import type { User, Server, Channel } from '../types'
import type { HistorySessionListItem, HistorySessionDetail, CallHistoryItem } from './historyTypes'
import { BACKEND_URL } from './config'
import { detectClientInfo } from '../utils/clientInfo'
import { getRequestActiveServer } from './activeServer'

/** Per-request client identity so the server labels login sessions
 *  ("Rx2Go Desktop" etc.) instead of guessing from the WebView User-Agent. */
export function clientHeaders(): Record<string, string> {
  const info = detectClientInfo()
  const client = info.isElectron ? 'desktop' : info.isMobile ? 'mobile' : 'web'
  return { 'X-Client': client, 'X-App-Version': info.appVersion ?? '' }
}

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  const guestToken = localStorage.getItem('guestToken')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  } else if (guestToken) {
    const tokenValue = guestToken.startsWith('guest:') ? guestToken : `guest:${guestToken}`
    config.headers.Authorization = `Bearer ${tokenValue}`
  }
  const activeServerId = getRequestActiveServer()
  const requestServerId = config.headers['X-Server-Id'] ?? config.headers['x-server-id']
  if (activeServerId && requestServerId === undefined) {
    config.headers['X-Server-Id'] = activeServerId
  }
  Object.assign(config.headers, clientHeaders())
  return config
})

/** Drop the local session tokens (access + refresh). Guest tokens are
 *  managed separately by guestStore and left untouched. */
function clearAuthTokens(): void {
  localStorage.removeItem('token')
  localStorage.removeItem('refreshToken')
}

// OSPROD-240: single-flight access-token refresh. When the access token
// expires, the server 401s; instead of logging the user out we swap the
// refresh token for a new access token and replay the original request.
// A single shared promise collapses a burst of concurrent 401s (many
// in-flight requests, multiple tabs) into ONE /auth/refresh call.
let refreshInFlight: Promise<string | null> | null = null

async function requestNewAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refreshToken')
  if (!refreshToken) return null
  try {
    // Bare axios (not `api`) so this call skips the interceptors below and
    // can't recurse if the refresh itself 401s.
    const res = await axios.post(
      `${BACKEND_URL}/api/auth/refresh`,
      { refreshToken },
      { headers: clientHeaders() },
    )
    const newToken = res.data?.token
    if (typeof newToken === 'string' && newToken) {
      localStorage.setItem('token', newToken)
      // OSPROD-270: let long-lived listeners (the socket singleton) swap
      // their captured auth token for the fresh one. Without this the
      // socket keeps reconnecting with the OLD access token, the server's
      // handshake jwt.verify rejects it, socket.io treats the middleware
      // denial as fatal (no auto-retry) — and every realtime surface
      // (unread badges, toasts, message sound) silently dies until a full
      // page reload. Worst on the desktop app, whose page lives for days.
      try {
        window.dispatchEvent(new CustomEvent('rx:token-refreshed', { detail: { token: newToken } }))
      } catch { /* non-window env (tests) — ignore */ }
      return newToken
    }
    return null
  } catch {
    return null
  }
}

export function refreshAccessToken(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = requestNewAccessToken().finally(() => { refreshInFlight = null })
  }
  return refreshInFlight
}

function redirectToLogin(query: string): void {
  if (!window.location.pathname.startsWith('/login')) {
    window.location.assign(`/login${query}`)
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status
    const code = error.response?.data?.error
    const original = error.config
    if (status !== 401 || !original) {
      return Promise.reject(error)
    }

    // Hard-logout codes — the session is intentionally dead; refreshing
    // would just 401 again. Drop tokens and bounce to /login immediately.
    if (code === 'account_disabled') {
      clearAuthTokens()
      redirectToLogin('?disabled=1')
      return Promise.reject(error)
    }
    if (code === 'session_revoked') {
      clearAuthTokens()
      redirectToLogin('?expired=1')
      return Promise.reject(error)
    }

    // The refresh endpoint itself 401ing (or a request we've already
    // retried once) means the refresh path is exhausted — don't loop.
    const url: string = original.url || ''
    if (url.includes('/auth/refresh') || original._retry) {
      return Promise.reject(error)
    }

    // No refresh token (legacy pre-OSPROD-240 session, or a guest) — keep
    // the old behavior: clear the stale access token and reject.
    if (!localStorage.getItem('refreshToken')) {
      localStorage.removeItem('token')
      return Promise.reject(error)
    }

    // Access token expired but the session is (probably) still alive:
    // silently mint a new access token and replay the original request.
    original._retry = true
    const newToken = await refreshAccessToken()
    if (newToken) {
      // The request interceptor re-reads localStorage('token'), so the
      // replay carries the fresh token automatically.
      return api(original)
    }

    // Refresh failed → the session is really gone. Log out.
    clearAuthTokens()
    redirectToLogin('?expired=1')
    return Promise.reject(error)
  }
)

export default api

export const authApi = {
  registerStart: (data: { email: string; username: string; firstName: string; lastName?: string; position?: string; birthday: string; password: string; inviteCode: string }) => api.post('/auth/register/start', data),
  registerVerify: (data: { sessionId: string; code: string }) => api.post('/auth/register/verify', data),
  registerResend: (data: { sessionId: string }) => api.post('/auth/register/resend', data),
  login: (data: { email: string; password: string; trustedDeviceToken?: string }) => api.post('/auth/login', data),
  // OSPROD-180: second login step for 2FA accounts.
  login2fa: (data: { challengeId: string; code: string; trustDevice?: boolean }) =>
    api.post<{ token: string; refreshToken?: string; user: User; trustedDeviceToken?: string }>('/auth/login/2fa', data),
  // OSPROD-240: exchange a refresh token for a fresh access token.
  refresh: (refreshToken: string) => api.post<{ token: string }>('/auth/refresh', { refreshToken }),
  // ViewSX self-service signup: exchange the one-time '#signup=<code>'
  // fragment (Login.tsx, on mount) for a normal login session. Same
  // {token, refreshToken, user} shape as /login, plus the provisioned
  // workspace id to navigate straight into.
  signupExchange: (code: string) =>
    api.post<{ token: string; refreshToken?: string; user: User; serverId: string }>('/auth/signup-exchange', { code }),
  loginWithGoogle: (data: { idToken: string }) => api.post('/auth/google', data),
  activateInfo: (token: string) =>
    api.get<{ user: { id: string; email: string; firstName: string; lastName: string | null; position: string | null; avatarUrl: string | null; accountStatus: string } }>(
      `/auth/activate-info?token=${encodeURIComponent(token)}`,
    ),
  activate: (data: { token: string; password: string }) => api.post('/auth/activate', data),
  // Self-service password reset. forgotPassword ALWAYS resolves 200 with
  // a neutral { ok: true } (no account-existence disclosure); resetPassword
  // redeems the emailed token to set a new password (no JWT — user re-logs in).
  forgotPassword: (email: string) => api.post<{ ok: true }>('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) =>
    api.post<{ success: true }>('/auth/reset-password', { token, password }),
  me: () => api.get('/auth/me'),
  updateMe: (data: Partial<User>) => api.put('/auth/me', data),
  changePassword: (data: { currentPassword: string; newPassword: string }) => api.post('/auth/change-password', data),
  // OSPROD-179: user's own phone number + SMS verification.
  phone: {
    // Set/replace the number → server saves it (unverified) + sends a code.
    start: (phoneNumber: string) =>
      api.post<{ ok: true; phoneNumber: string; cooldownSec: number; ttlMinutes: number }>('/auth/phone', { phoneNumber }),
    resend: () => api.post<{ ok: true; cooldownSec: number }>('/auth/phone/resend', {}),
    verify: (code: string) =>
      api.post<{ ok: true; phoneNumber: string; phoneVerified: true }>('/auth/phone/verify', { code }),
    remove: () => api.delete<{ ok: true; phoneNumber: null; phoneVerified: false }>('/auth/phone'),
  },
  // OSPROD-180: SMS two-factor management (the login challenge itself is
  // login2fa above).
  twoFactor: {
    enable: () => api.post<{ ok: true; twoFactorEnabled: true }>('/auth/2fa/enable', {}),
    disableStart: (password: string) =>
      api.post<{ ok: true; codeSent?: boolean; twoFactorEnabled?: boolean; phoneHint?: string }>('/auth/2fa/disable/start', { password }),
    disable: (code: string) =>
      api.post<{ ok: true; twoFactorEnabled: false }>('/auth/2fa/disable', { code }),
    listTrustedDevices: () =>
      api.get<{ devices: Array<{ id: string; label: string | null; createdAt: string; lastUsedAt: string; expiresAt: string }> }>('/auth/2fa/trusted-devices'),
    revokeTrustedDevice: (id: string) => api.delete<{ ok: true }>(`/auth/2fa/trusted-devices/${id}`),
    revokeAllTrustedDevices: () => api.delete<{ ok: true }>('/auth/2fa/trusted-devices'),
  },
  sessions: {
    list: () => api.get<{ sessions: UserSessionView[] }>('/auth/sessions'),
    revoke: (id: string) => api.delete(`/auth/sessions/${id}`),
    revokeOthers: () => api.delete('/auth/sessions?exceptCurrent=1'),
    pushDevices: () => api.get<{ devices: PushDeviceView[] }>('/notifications/push/devices'),
    removePushDevice: (id: string) => api.delete(`/notifications/push/devices/${id}`),
    probes: () => api.get<{ devices: MyProbeDevice[] }>('/probe/devices'),
    unpairProbe: (id: string) => api.delete(`/probe/devices/${id}`),
  },
  logout: () => api.post('/auth/logout'),
}

export interface UserSessionView {
  id: string
  clientType: string
  label: string | null
  ip: string | null
  createdAt: string
  lastSeenAt: string
  current: boolean
  revokedAt: string | null
}

export interface PushDeviceView {
  id: string
  platform: string
  deviceName: string | null
  appVersion: string | null
  lastSeenAt: string
}

export interface MyProbeDevice {
  id: string
  name: string
  os: string | null
  online: boolean
  lastSeenAt: string | null
  isMine: boolean
}

export interface ApiKey {
  id: string
  name: string
  prefix: string
  scopes: string[]
  lastUsedAt: string | null
  expiresAt: string | null
  revokedAt: string | null
  createdAt: string
}

export const API_SCOPES = ['messages:write', 'messages:read', 'channels:read', 'channels:write', 'reactions:write'] as const

export const apiKeysApi = {
  list: () => api.get<ApiKey[]>('/api-keys'),
  create: (data: { name: string; scopes: string[]; expiresAt?: string }) =>
    api.post<ApiKey & { token: string }>('/api-keys', data),
  revoke: (id: string) => api.post(`/api-keys/${id}/revoke`),
  remove: (id: string) => api.delete(`/api-keys/${id}`),
}

export const serverApi = {
  list: () => api.get('/servers'),
  get: (id: string) => api.get(`/servers/${id}`),
  create: (data: { name: string; iconUrl?: string }) => api.post('/servers', data),
  update: (id: string, data: Partial<Server>) => api.put(`/servers/${id}`, data),
  delete: (id: string) => api.delete(`/servers/${id}`),
  getInvites: (id: string) => api.get(`/servers/${id}/invites`),
  createInvite: (id: string, data?: { maxUses?: number; expiresAt?: string }) => api.post(`/servers/${id}/invites`, data),
  deleteInvite: (serverId: string, inviteId: string) => api.delete(`/servers/${serverId}/invites/${inviteId}`),
  getRoles: (id: string) => api.get(`/servers/${id}/roles`),
  createRole: (id: string, data: { name: string; color?: string; permissions?: string }) => api.post(`/servers/${id}/roles`, data),
  updateRole: (serverId: string, roleId: string, data: { name?: string; color?: string; permissions?: string }) => api.put(`/servers/${serverId}/roles/${roleId}`, data),
  deleteRole: (serverId: string, roleId: string) => api.delete(`/servers/${serverId}/roles/${roleId}`),
  members: (id: string) =>
    api.get<Array<{
      userId: string
      user: { id: string; email: string; avatarUrl: string | null; firstName?: string; lastName?: string }
    }>>(`/servers/${id}/members`),
  updateMemberRoles: (serverId: string, userId: string, roleIds: string[]) => api.put(`/servers/${serverId}/members/${userId}/roles`, { roleIds }),
  kickMember: (serverId: string, userId: string) => api.delete(`/servers/${serverId}/members/${userId}`),
  banMember: (serverId: string, data: { userId: string; reason?: string }) => api.post(`/servers/${serverId}/bans`, data),
  createCategory: (id: string, data: { name: string; type?: string }) => api.post(`/servers/${id}/categories`, data),
  updateCategory: (id: string, categoryId: string, data: { name: string }) => api.put(`/servers/${id}/categories/${categoryId}`, data),
  deleteCategory: (id: string, categoryId: string) => api.delete(`/servers/${id}/categories/${categoryId}`),
  listDepartmentChannelAdmins: (serverId: string, deptId: string) =>
    api.get(`/servers/${serverId}/departments/${deptId}/channel-admins`),
  addDepartmentChannelAdmin: (serverId: string, deptId: string, userId: string) =>
    api.post(`/servers/${serverId}/departments/${deptId}/channel-admins`, { userId }),
  removeDepartmentChannelAdmin: (serverId: string, deptId: string, userId: string) =>
    api.delete(`/servers/${serverId}/departments/${deptId}/channel-admins/${userId}`),
  /** All pending waiting-room entries on this server, grouped by channelId.
   *  Returns only channels where the caller has voice-host rights. */
  waitingEntries: (id: string) =>
    api.get<Record<string, Array<{
      id: string
      channelId: string
      userId: string
      status: string
      requestedAt: string
      user: { id: string; username: string; avatarUrl: string | null }
    }>>>(`/servers/${id}/waiting-entries`),
}

// Server-admin specific endpoints — paginated/searchable + bulk
// operations introduced for the rewritten Members tab. Older
// `serverApi.members` returns the whole list as a flat array; kept
// for back-compat with other call sites.
export type ServerMemberStatus = 'all' | 'DRAFT' | 'INVITED' | 'ACTIVE' | 'DISABLED'
export type ServerMemberSort = 'name' | 'joinedAt' | 'lastSeen'

export interface ServerMemberRow {
  id: string
  userId: string
  serverId: string
  nickname: string | null
  joinedAt: string
  banned: boolean
  /** Primary department — org chart, approver routing, sidebar grouping. */
  departmentId: string | null
  /** OSPROD-275: every department, primary first. Absent on rows written
   *  before the column existed; read via memberDepartments(). */
  departmentIds?: string[] | null
  roles: Array<{
    id: string
    role: { id: string; name: string; color: string; permissions: string }
  }>
  user: {
    id: string
    email: string
    username: string
    firstName: string
    lastName: string | null
    position: string | null
    avatarUrl: string | null
    accountStatus: 'DRAFT' | 'INVITED' | 'ACTIVE' | 'DISABLED'
    inviteSentAt: string | null
    cellPhone: string | null
    locationCountry: string | null
    hireDate: string | null
    lastSeenAt: string | null
    status?: string
    // Org fields for the shared PeopleEditModal (Server Admin > Members).
    // Carried so an edit doesn't null them on save — see memberAdapters.
    managerId: string | null
    aboutMe: string | null
  } | null
}

export interface ServerMemberPageResponse {
  members: ServerMemberRow[]
  total: number
  page: number
  pageSize: number
  statusCounts: Partial<Record<'DRAFT' | 'INVITED' | 'ACTIVE' | 'DISABLED', number>>
}

export const serverAdminApi = {
  membersPage: (serverId: string, params: {
    q?: string
    status?: ServerMemberStatus
    deptId?: string | 'none'
    roleId?: string | 'none'
    page?: number
    pageSize?: number
    sort?: ServerMemberSort
  }) => api.get<ServerMemberPageResponse>(`/servers/${serverId}/members.page`, { params }),

  bulkRoles: (
    serverId: string,
    body: {
      userIds: string[]
      addRoleIds?: string[]
      removeRoleIds?: string[]
      setRoleIds?: string[]
    },
  ) => api.post<{ updated: number; members: string[] }>(
    `/servers/${serverId}/members/bulk-roles`,
    body,
  ),

  bulkDepartment: (serverId: string, body: { userIds: string[]; departmentId: string | null }) =>
    api.post<{ updated: number }>(`/servers/${serverId}/members/bulk-department`, body),
}

export const channelApi = {
  create: (serverId: string, data: {
    name: string;
    type: 'TEXT' | 'VOICE';
    categoryId?: string;
    // OSPROD-131: optional description (→ Channel.topic) and pre-invited
    // members. Non-empty memberUserIds makes the new channel private
    // (server-side: isPrivate=true + VIEW_CHANNEL overwrite per user).
    topic?: string;
    memberUserIds?: string[];
  }) => api.post(`/channels/server/${serverId}`, data),
  update: (id: string, data: Partial<Channel>) => api.put(`/channels/${id}`, data),
  delete: (id: string) => api.delete(`/channels/${id}`),
  messages: (id: string, params?: { limit?: number; before?: string }) =>
    api.get(`/channels/${id}/messages`, { params }),
  voiceStates: (id: string) => api.get(`/channels/${id}/voice`),
  voiceStatesForServer: (serverId: string) => api.get(`/channels/server/${serverId}/voice`),
  kickUser: (channelId: string, userId: string) => api.post(`/channels/${channelId}/kick-user`, { userId }),
  moveUser: (channelId: string, userId: string, targetChannelId: string) =>
    api.post(`/channels/${channelId}/move-user`, { userId, targetChannelId }),
  /** Toggle waiting-room mode for a voice channel. Host-only. */
  setLocked: (id: string, isLocked: boolean) =>
    api.put<{ id: string; isLocked: boolean }>(`/channels/${id}/lock`, { isLocked }),
  /** Whether the current user has voice-host rights on this channel. */
  hostStatus: (id: string) =>
    api.get<{ isHost: boolean; canManageAdmins: boolean }>(`/channels/${id}/host-status`),
  /** List the channel owner + extra admins. Voice-host only. */
  getAdmins: (id: string) =>
    api.get<{
      owner: { id: string; username: string; avatarUrl: string | null; firstName?: string; lastName?: string } | null
      admins: Array<{ id: string; user: { id: string; username: string; avatarUrl: string | null; firstName?: string; lastName?: string }; createdAt: string }>
    }>(`/channels/${id}/admins`),
  /** Add a channel admin. Owner-level only. */
  addAdmin: (id: string, userId: string) =>
    api.post<{ id: string; user: { id: string; username: string; avatarUrl: string | null }; createdAt: string }>(`/channels/${id}/admins`, { userId }),
  /** Remove a channel admin. Owner-level only. */
  removeAdmin: (id: string, userId: string) => api.delete(`/channels/${id}/admins/${userId}`),
  // Message-tag vocabulary (owner-curated) + apply/pin (host).
  listMessageTags: (id: string) => api.get(`/channels/${id}/message-tags`),
  createMessageTag: (id: string, data: { label: string; color?: string | null }) => api.post(`/channels/${id}/message-tags`, data),
  updateMessageTag: (id: string, tagId: string, data: { label?: string; color?: string | null; position?: number }) => api.patch(`/channels/${id}/message-tags/${tagId}`, data),
  deleteMessageTag: (id: string, tagId: string) => api.delete(`/channels/${id}/message-tags/${tagId}`),
  tagMessage: (channelId: string, messageId: string, tagDefId: string) => api.post(`/channels/${channelId}/messages/${messageId}/tags`, { tagDefId }),
  untagMessage: (channelId: string, messageId: string, tagId: string) => api.delete(`/channels/${channelId}/messages/${messageId}/tags/${tagId}`),
  pinMessage: (channelId: string, messageId: string, pinned: boolean) => api.post(`/channels/${channelId}/messages/${messageId}/pin`, { pinned }),
  /** Ban a participant from a voice channel. minutes:null = permanent. Host-only. */
  banUser: (channelId: string, data: { userId?: string; identity?: string; subjectType?: 'USER' | 'GUEST' | 'PHONE'; displayName?: string; minutes: number | null; reason?: string }) =>
    api.post<{ success: boolean; ban: { id: string; expiresAt: string | null } }>(`/channels/${channelId}/ban`, data),
  /** Lift a ban (soft-delete). Host-only. */
  unbanUser: (channelId: string, banId: string) => api.delete(`/channels/${channelId}/ban/${banId}`),
  /** List active bans. Host-only. */
  getBans: (channelId: string) =>
    api.get<{ bans: Array<{ id: string; subjectType: 'USER' | 'GUEST' | 'PHONE'; identity: string; user: { id: string; username: string; avatarUrl: string | null; firstName?: string; lastName?: string } | null; displayName: string | null; reason: string | null; bannedAt: string; expiresAt: string | null }> }>(`/channels/${channelId}/bans`),
  /** List lobby-bypass entries for a voice channel. Host/owner only. */
  getBypass: (channelId: string) =>
    api.get<{ entries: Array<{ id: string; userId?: string; departmentId?: string; createdAt: string }> }>(`/channels/${channelId}/bypass`),
  /** Add a lobby-bypass entry (userId XOR departmentId). Host/owner only. */
  addBypass: (channelId: string, body: { userId?: string; departmentId?: string }) =>
    api.post<{ id: string; userId?: string; departmentId?: string; createdAt: string }>(`/channels/${channelId}/bypass`, body),
  /** Remove a lobby-bypass entry by its id. Host/owner only. */
  removeBypass: (channelId: string, entryId: string) => api.delete(`/channels/${channelId}/bypass/${entryId}`),
}

// Live polls (voting) in voice channels. Authoring + run/vote/history.
export const pollApi = {
  list: (channelId: string) => api.get(`/channels/${channelId}/polls`),
  create: (channelId: string, data: {
    title: string; isAnonymous: boolean;
    questions: { kind: 'OPEN' | 'CLOSED'; text: string; options?: string[] }[]
  }) => api.post<{ id: string }>(`/channels/${channelId}/polls`, data),
  update: (channelId: string, setId: string, data: {
    title: string; isAnonymous: boolean;
    questions: { kind: 'OPEN' | 'CLOSED'; text: string; options?: string[] }[]
  }) => api.put<{ id: string }>(`/channels/${channelId}/polls/${setId}`, data),
  remove: (channelId: string, setId: string) => api.delete(`/channels/${channelId}/polls/${setId}`),
  run: (channelId: string, setId: string) => api.post(`/channels/${channelId}/polls/${setId}/run`, {}),
  patchRun: (channelId: string, runId: string, data: { activeQuestionId?: string; state?: 'ACTIVE' | 'CLOSED' }) =>
    api.patch(`/channels/${channelId}/poll-runs/${runId}`, data),
  vote: (channelId: string, runId: string, data: { questionId: string; optionId?: string; text?: string }) =>
    api.post(`/channels/${channelId}/poll-runs/${runId}/vote`, data),
  activeRun: (channelId: string) => api.get(`/channels/${channelId}/poll-runs/active`),
  history: (channelId: string) => api.get(`/channels/${channelId}/poll-runs`),
  runResults: (channelId: string, runId: string) =>
    api.get(`/channels/${channelId}/poll-runs/${runId}/results`),
}

// Synchronized PDF presentations in voice channels. Upload/render + run/slide.
export const presentationApi = {
  list: (channelId: string) => api.get(`/channels/${channelId}/presentations`),
  upload: (channelId: string, form: FormData) =>
    api.post<{ id: string; slideCount: number }>(`/channels/${channelId}/presentations`, form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  remove: (channelId: string, pid: string) => api.delete(`/channels/${channelId}/presentations/${pid}`),
  run: (channelId: string, pid: string) => api.post(`/channels/${channelId}/presentations/${pid}/run`, {}),
  patchRun: (channelId: string, runId: string, data: { currentSlideIndex?: number; state?: 'ACTIVE' | 'CLOSED' }) =>
    api.patch(`/channels/${channelId}/presentation-runs/${runId}`, data),
  activeRun: (channelId: string) => api.get(`/channels/${channelId}/presentation-runs/active`),
}

export const recordingApi = {
  start: (channelId: string) => api.post('/recordings/start', { channelId }),
  stop: (id: string) => api.post(`/recordings/${id}/stop`),
  list: (channelId: string) => api.get('/recordings', { params: { channelId } }),
  get: (id: string) => api.get(`/recordings/${id}`),
  remove: (id: string) => api.delete(`/recordings/${id}`),
  // Access-gated download/playback URL. A plain <a>/<audio> can't send the
  // Bearer header, so carry the JWT in the `?token=` query the auth middleware
  // accepts (mirrors filesApi.blobUrl). Recordings are no longer reachable via
  // the public /uploads path (P4).
  streamUrl: (id: string) => {
    let token = ''
    try { token = localStorage.getItem('token') || '' } catch { /* SSR/no storage */ }
    return `${BACKEND_URL}/api/recordings/${id}/stream${token ? `?token=${encodeURIComponent(token)}` : ''}`
  },
}

export const messageApi = {
  // Channel send mirrors dmApi.send so the polymorphic Telegram*
  // composer can route stickers / GIFs / voice / video / photo /
  // attachments through either transport without a feature gap.
  // Server-side schema (routes/messages.ts) accepts the same field set
  // and requires at least one payload bearer (content, media, sticker,
  // gif, or attachment).
  send: (data: {
    channelId: string
    content?: string
    replyToId?: string
    audioUrl?: string
    audioDuration?: number
    videoUrl?: string
    videoDuration?: number
    photoUrl?: string
    photoWidth?: number
    photoHeight?: number
    stickerId?: string
    gifUrl?: string
    gifWidth?: number
    gifHeight?: number
    attachments?: Array<{ fileName: string; fileUrl: string; fileSize: number; mimeType: string }>
    localId?: string
    // OSPROD-66: optional recipient subset for voice-channel privates.
    // Omitted means "everyone in the call".
    recipientIds?: string[]
    forwardedFromName?: string
    forwardedFromMessageId?: string
  }) => api.post('/messages', data),
  edit: (id: string, data: { content: string }) => api.put(`/messages/${id}`, data),
  delete: (id: string) => api.delete(`/messages/${id}`),
  react: (id: string, emoji: string) => api.post(`/messages/${id}/reactions`, { emoji }),
}

export const userApi = {
  search: (q: string) => api.get('/users', { params: { q } }),
  get: (id: string) => api.get(`/users/${id}`),
}

export interface CrmProjectListItem {
  id: string
  name: string
  status: string
  companyId: string | null
  companyName: string | null
  ownerUserId: string
  ownerName: string
  startedAt: string | null
  targetGoLiveAt: string | null
  createdAt: string
  updatedAt: string
}

export interface CrmProjectCreateInput {
  name: string
  ownerUserId: string
  companyId?: string | null
  description?: string | null
  status?: 'active' | 'paused' | 'done' | 'archived'
  startedAt?: string | null
  targetGoLiveAt?: string | null
  ourBoardId?: string | null
  clientBoardId?: string | null
  trackerProjectId?: string | null
}

export interface CrmProjectRecordTask {
  side: 'our' | 'client'
  title: string
  status: string
  assignee: string | null
  dueDate: string | null
  priority: string | null
}

export interface CrmProjectRecord {
  project: {
    id: string
    name: string
    status: string
    ownerUserId: string
    companyId: string | null
    description: string | null
    startedAt: string | null
    targetGoLiveAt: string | null
    createdAt: string
    updatedAt: string
  }
  owner: { id: string; name: string } | null
  participants: {
    id: string
    projectId: string
    userId: string | null
    personId: string | null
    side: string
    role: string
    resolvedName: string | null
  }[]
  links: {
    id: string
    system: string
    side: string
    externalId: string
    boardName?: string | null
  }[]
  tasks: { our: CrmProjectRecordTask[]; client: CrmProjectRecordTask[] }
  milestones: {
    id: string
    title: string
    description: string | null
    dueAt: string | null
    status: string
  }[]
  meetings: {
    interactionId: string
    type: string
    startedAt: string | null
    subject: string | null
  }[]
  time: {
    rows: { memberId: string; memberName: string; hours: number }[]
    costNote: string
  } | null
}

export const crmProjectApi = {
  list: () => api.get<CrmProjectListItem[]>('/crm/projects'),
  get: (id: string) => api.get(`/crm/projects/${id}`),
  record: (id: string) => api.get<CrmProjectRecord>(`/crm/projects/${id}/record`),
  create: (data: CrmProjectCreateInput) => api.post<{ id: string }>('/crm/projects', data),
  update: (id: string, data: Partial<CrmProjectCreateInput>) => api.patch(`/crm/projects/${id}`, data),
  remove: (id: string) => api.delete(`/crm/projects/${id}`),
}

export type CrmClientKind = 'client' | 'partner' | 'newsletter'
export type CrmReciprocityStatus = 'active' | 'prospect' | 'unknown'

export interface CrmClientListItem {
  key: string // registrable domain — the group id
  name: string
  domain: string
  subdomains: string[]
  peopleCount: number
  status: CrmReciprocityStatus // reciprocity rollup (engine)
  kind: CrmClientKind | null // manual classification
  lastContactAt: string | null
}

export const crmClientApi = {
  list: () => api.get<CrmClientListItem[]>('/crm/clients'),
  get: (key: string) => api.get(`/crm/clients/${encodeURIComponent(key)}`),
  classify: (key: string, kind: CrmClientKind | null) =>
    api.post(`/crm/clients/${encodeURIComponent(key)}/classify`, { kind }),
}

export interface CrmProposalEvidence {
  interactionIds: string[]
  personIds: string[]
  emailCount: number
  spanDays: number
}

export interface CrmProposalListItem {
  id: string
  suggestedName: string
  companyId: string | null
  companyName: string | null
  aiSummary: string
  evidence: CrmProposalEvidence | null
  confidence: number | null
  createdAt: string
}

export const crmProposalApi = {
  list: () => api.get<CrmProposalListItem[]>('/crm/proposals'),
  accept: (id: string) => api.post<{ projectId: string }>(`/crm/proposals/${id}/accept`),
  reject: (id: string) => api.post<{ ok: boolean }>(`/crm/proposals/${id}/reject`),
}

// Thread-cluster client detection (2026-06-25). Candidate clients detected from
// email threads, surfaced as a confidence-ranked moderation queue.
export type CrmClusterStatus = 'candidate' | 'confirmed' | 'rejected' | 'merged'
export type CrmClusterKind = 'client' | 'partner' | 'noise'

export interface CrmClusterParty {
  email: string
  domain: string | null
  name: string | null
}

// A firm's contact/employee in the detail view, enriched from correspondence.
export interface CrmClusterContact extends CrmClusterParty {
  isPrimary: boolean
  title: string | null
  phone: string | null
  role: string | null
  repliesCount: number
  isRosterContact: boolean       // true once 3+ replies (or manually added)
  source: string                 // auto | manual
  onVacationUntil: string | null
}

export interface CrmClusterListItem {
  id: string
  status: CrmClusterStatus
  displayName: string | null
  kind: CrmClusterKind | null
  isVip: boolean
  confidence: number
  priority: number
  reasoning: string | null
  threadCount: number
  rosterCount: number            // # of confirmed roster contacts (3+ replies)
  parties: CrmClusterParty[]
  updatedAt: string
}

export interface CrmClusterDetail {
  id: string
  status: CrmClusterStatus
  displayName: string | null
  kind: CrmClusterKind | null
  isVip: boolean
  confidence: number
  priority: number
  reasoning: string | null
  summary: string | null
  threads: { threadExternalId: string; joinKey: string }[]
  parties: CrmClusterContact[]
}

// AI-proposed group of clusters that are the same real firm (name variants /
// extra domains), for one-click human-approved merge.
export interface CrmMergeSuggestion {
  clusterIds: string[]
  canonicalName: string
  reason: string
  names: string[]
}

export const crmClusterApi = {
  list: (status: CrmClusterStatus = 'candidate') =>
    api.get<CrmClusterListItem[]>(`/crm/clusters?status=${encodeURIComponent(status)}`),
  get: (id: string) => api.get<CrmClusterDetail>(`/crm/clusters/${encodeURIComponent(id)}`),
  confirm: (id: string, kind: 'client' | 'partner' = 'client') =>
    api.post<{ id: string; status: 'confirmed'; kind: string }>(`/crm/clusters/${encodeURIComponent(id)}/confirm`, { kind }),
  reject: (id: string) =>
    api.post<{ id: string; status: 'rejected' }>(`/crm/clusters/${encodeURIComponent(id)}/reject`),
  rename: (id: string, displayName: string, kind?: 'client' | 'partner' | 'noise') =>
    api.post<{ id: string; displayName: string }>(`/crm/clusters/${encodeURIComponent(id)}/rename`, { displayName, kind }),
  setVip: (id: string, vip: boolean) =>
    api.post<{ id: string; isVip: boolean }>(`/crm/clusters/${encodeURIComponent(id)}/vip`, { vip }),
  markInternal: (id: string) =>
    api.post<{ id: string; status: 'internal'; ownDomains: string[] }>(`/crm/clusters/${encodeURIComponent(id)}/internal`),
  // Fold ids[1..] into ids[0]; the primary keeps everything, names get aliased.
  mergeMany: (ids: string[]) =>
    api.post<{ into: string; merged: string[] }>(`/crm/clusters/merge-many`, { ids }),
  suggestMerges: () =>
    api.get<{ groups: CrmMergeSuggestion[]; error?: string }>(`/crm/clusters/suggest-merges`),
  // Client 360: communication timeline + open follow-ups + linked projects.
  activity: (id: string) =>
    api.get<CrmClusterActivity>(`/crm/clusters/${encodeURIComponent(id)}/activity`),
  // Client 360: full aggregation (timeline incl calls + contacts + projects + follow-ups).
  client360: (id: string) =>
    api.get<CrmClient360>(`/crm/client-360/${encodeURIComponent(id)}`),
  // client-access #0: promote a cluster -> durable CrmClient (serverId from X-Server-Id header)
  promote: (clusterId: string) =>
    api.post<{ id: string; displayName: string; source: string; alreadyLinked?: boolean }>(
      `/api/crm/client-access/clusters/${clusterId}/promote`, {}),
}

export interface CrmClientContact { email: string; name: string | null; title: string | null; hasAccess: boolean }
export interface CrmClientDetail {
  id: string; displayName: string; status: string; source: string
  serverId: string; clusterId: string | null; domainKeys: string[]; contacts: CrmClientContact[]
}

export const crmClientAccessApi = {
  create: (displayName: string) =>
    api.post<{ id: string; displayName: string; status: string; source: string }>(
      '/api/crm/client-access/clients', { displayName }),
  get: (id: string) => api.get<CrmClientDetail>(`/api/crm/client-access/clients/${id}`),
}

export interface CrmClusterActivity {
  timeline: { interactionId: string; type: 'email' | 'meeting' | 'other'; at: string | null; subject: string | null; digest: string }[]
  commitments: { description: string; ownerSide: string; dueAt: string | null; status: string }[]
  projects: { id: string; name: string; status: string }[]
}

export interface CrmClient360 {
  identity: { id: string; displayName: string | null; kind: string | null; status: string; isVip: boolean; confidence: number }
  contacts: {
    email: string; name: string | null; title: string | null; phone: string | null; role: string | null
    repliesCount: number; isRosterContact: boolean; source: string; onVacationUntil: string | null; isPrimary: boolean
  }[]
  timeline: {
    key: string; type: 'email' | 'meeting' | 'call' | 'other'; at: string | null; subject: string | null; digest: string
    direction?: 'INBOUND' | 'OUTBOUND'; durationSec?: number | null
  }[]
  calls: {
    id: string; direction: 'INBOUND' | 'OUTBOUND'; status: string; startedAt: string; durationSec: number | null
    contactEmail: string | null; contactName: string | null; counterpartyNumber: string
  }[]
  projects: { id: string; name: string; status: string; linkSource: 'auto' | 'manual' }[]
  followUps: { description: string; ownerSide: string; dueAt: string | null; status: string }[]
}

// Our own companies/domains — excluded from client detection (user-managed).
export interface CrmOwnCompany {
  id: string
  domain: string
  label: string | null
}

export const ownCompanyApi = {
  list: () => api.get<CrmOwnCompany[]>('/crm/own-companies'),
  add: (domain: string, label?: string) => api.post<CrmOwnCompany>('/crm/own-companies', { domain, label }),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/crm/own-companies/${encodeURIComponent(id)}`),
}

// Keywords in a company name that auto-confirm it as a client (e.g. hospital, pharmacy).
export interface CrmClientKeyword {
  id: string
  keyword: string
  label: string | null
}

export const clientKeywordApi = {
  list: () => api.get<CrmClientKeyword[]>('/crm/client-keywords'),
  add: (keyword: string, label?: string) => api.post<CrmClientKeyword>('/crm/client-keywords', { keyword, label }),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/crm/client-keywords/${encodeURIComponent(id)}`),
}

// OSPROD-79: org-chart endpoints. List is open to any authenticated
// user (every member needs to see the company tree); create / update
// / delete / assign are admin-only on the server.
export const departmentApi = {
  list: (serverId: string) => api.get(`/servers/${serverId}/departments`),
  create: (serverId: string, data: { name: string; parentId?: string | null; headUserId?: string | null; reportsToUserId?: string | null }) =>
    api.post(`/servers/${serverId}/departments`, data),
  update: (serverId: string, id: string, data: { name?: string; parentId?: string | null; headUserId?: string | null; reportsToUserId?: string | null }) =>
    api.patch(`/servers/${serverId}/departments/${id}`, data),
  delete: (serverId: string, id: string) => api.delete(`/servers/${serverId}/departments/${id}`),
  assignMember: (serverId: string, userId: string, departmentId: string | null) =>
    api.put(`/servers/${serverId}/members/${userId}`, { departmentId }),
  // OSPROD-275: full membership set, primary FIRST (max 3, enforced server-side).
  // The server derives `departmentId` from departmentIds[0], so the two can
  // never drift apart.
  assignMemberDepartments: (serverId: string, userId: string, departmentIds: string[]) =>
    api.put(`/servers/${serverId}/members/${userId}`, { departmentIds }),
}

// Communication governance (Policies): server layer + department add-on layer.
export const governanceApi = {
  getServerPolicy: (serverId: string) => api.get(`/governance/servers/${serverId}/policy`),
  putServerPolicy: (serverId: string, data: { enforcementMode?: string; enabled?: boolean }) =>
    api.put(`/governance/servers/${serverId}/policy`, data),
  getDeptPolicy: (serverId: string, deptId: string) =>
    api.get(`/governance/servers/${serverId}/departments/${deptId}/policy`),
  putDeptPolicy: (serverId: string, deptId: string, data: { enforcementMode?: string; enabled?: boolean }) =>
    api.put(`/governance/servers/${serverId}/departments/${deptId}/policy`, data),
  createRule: (serverId: string, data: {
    departmentId?: string | null; type: 'topic' | 'keyword' | 'barrier'; name: string
    aiDescription?: string; keywords?: string[]; action?: 'notify' | 'block'
    surfaces: string[]; recipientIds?: string[]
    barrierA?: { kind: 'user' | 'dept'; id: string }; barrierB?: { kind: 'user' | 'dept'; id: string }
  }) => api.post(`/governance/servers/${serverId}/policy/rules`, data),
  updateRule: (serverId: string, ruleId: string, data: Record<string, unknown>) =>
    api.patch(`/governance/servers/${serverId}/policy/rules/${ruleId}`, data),
  deleteRule: (serverId: string, ruleId: string) =>
    api.delete(`/governance/servers/${serverId}/policy/rules/${ruleId}`),
  activity: (serverId: string, params: { departmentId?: string; ruleId?: string; surface?: string; cursor?: string; limit?: number }) =>
    api.get(`/governance/servers/${serverId}/policy/activity`, { params }),
}

// OSPROD-BOOK: shareable booking links. The public flavour
// (`publicBookingApi`) hits a different prefix that the server
// mounts without auth — used by the customer-facing /book/<slug>
// page where the visitor isn't logged into our app.
export type BookingDayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'
export interface BookingAvailabilityRange { start: string; end: string }
export type BookingWeeklyAvailability = Record<BookingDayKey, BookingAvailabilityRange[] | null>

export interface BookingLink {
  id: string
  userId: string
  slug: string
  title: string
  description: string | null
  durationMinutes: number
  bufferMinutes: number
  startsAt: string | null
  endsAt: string | null
  weeklyAvailability: BookingWeeklyAvailability
  /** IANA timezone — e.g. "America/New_York", "Europe/Moscow", "UTC". */
  timezone: string
  calendarSourceId: number | null
  isActive: boolean
  createdAt: string
  updatedAt: string
  _count?: { appointments: number }
}

export interface BookingAppointment {
  id: string
  bookingLinkId: string
  customerName: string
  customerEmail: string
  notes: string | null
  startsAt: string
  endsAt: string
  externalEventId: string | null
  status: string
  createdAt: string
}

export interface BookingLinkInput {
  slug?: string
  title: string
  description?: string | null
  durationMinutes: number
  bufferMinutes?: number
  startsAt?: string | null
  endsAt?: string | null
  weeklyAvailability: BookingWeeklyAvailability
  timezone?: string
  calendarSourceId?: number | null
  isActive?: boolean
}

/** Phase 2 onboarding — admin People panel. */
export type PeopleAccountStatus = 'DRAFT' | 'INVITED' | 'ACTIVE' | 'DISABLED'

export interface PeopleRow {
  id: string
  email: string
  firstName: string
  lastName: string | null
  position: string | null
  avatarUrl: string | null
  accountStatus: PeopleAccountStatus
  cellPhone: string | null
  locationCountry: string | null
  departmentName: string | null
  orgChartEmpId: string | null
  inviteSentAt: string | null
  hireDate: string | null
  birthday: string | null
  /** Nullable — accounts that never signed in have NULL until
   *  the first session writes it. Renderers should map null to a
   *  user-readable placeholder ("never"). */
  lastSeenAt: string | null
  aboutMe: string | null
  managerId: string | null
}

export interface PeopleListResponse {
  users: PeopleRow[]
  total: number
  page: number
  pageSize: number
  statusCounts: Partial<Record<PeopleAccountStatus, number>>
}

export interface DirectoryRow {
  empId: string
  userId: string
  email: string
  firstName: string
  lastName: string | null
  avatarUrl: string | null
  accountStatus: 'DRAFT' | 'INVITED' | 'ACTIVE' | 'DISABLED'
  hireDate: string | null
  position: string | null
  // Ride-along fields so the People-page Edit modal can hydrate
  // without a second round-trip. Empty for accounts that haven't
  // had these filled in yet (most DRAFTs).
  cellPhone: string | null
  locationCountry: string | null
  departmentName: string | null
  // Read-only activity meta — last sign-in + most recent invite
  // email. Both nullable; the modal shows them in a separate
  // read-only block at the bottom of the form.
  lastSeenAt: string | null
  inviteSentAt: string | null
  aboutMe: string | null
  managerId: string | null
}

/** Real org chart for the People page (GET /people/org). Departments +
 *  real members of the resolved server — the data-driven replacement for
 *  the static orgChart.ts mock. */
export interface OrgDept {
  id: string
  name: string
  parentId: string | null
  headUserId: string | null
  reportsToUserId: string | null
}
export interface OrgPerson {
  id: string
  firstName: string | null
  lastName: string | null
  position: string | null
  avatarUrl: string | null
  accountStatus: 'DRAFT' | 'INVITED' | 'ACTIVE' | 'DISABLED'
  departmentId: string | null
  managerId: string | null
  aboutMe: string | null
  email: string
  hireDate: string | null
  cellPhone: string | null
  locationCountry: string | null
  lastSeenAt: string | null
  inviteSentAt: string | null
}
export interface OrgPayload {
  serverId: string | null
  serverName: string | null
  departments: OrgDept[]
  people: OrgPerson[]
}

export interface VoicePresenceEntry {
  userId: string
  inVoice: boolean
  canJoin: boolean
  // Present only when canJoin (so a private dept channel isn't leaked).
  channelId?: string
  channelName?: string
  serverId?: string | null
}

export const voiceApi = {
  // Who is currently in a voice channel + whether THIS user can join each one.
  // Polled by the People page + public profile to surface "in <channel> · Join".
  presence: () => api.get<{ presence: VoicePresenceEntry[] }>('/voice/presence'),
}

export const peopleApi = {
  /** Real org chart (departments + members of the resolved server).
   *  Powers the People page tree — no mock data. Pass `serverId` to pin
   *  the org to a specific server (Server Admin uses this to build the
   *  member/manager list for the server it's editing, independent of the
   *  active-server header). */
  org: (serverId?: string) =>
    api.get<OrgPayload>('/people/org', serverId ? { params: { serverId } } : undefined),

  /** Legacy empId-keyed overlay (imported users only). Retained for any
   *  callers still keyed on orgChartEmpId; the People page now uses org(). */
  directory: () => api.get<DirectoryRow[]>('/people/directory'),

  /** Channels the given org-chart employee owns or admins. Empty if
   *  the empId hasn't been imported into User yet. */
  channels: (empId: string) =>
    api.get<Array<{ id: string; name: string; role: 'owner' | 'admin' }>>(
      `/people/${encodeURIComponent(empId)}/channels`,
    ),

  /** Edit an employee from the People page. Gated server-side on
   *  `canManagePeople` — global admins, server owners, or
   *  ADMINISTRATOR/MANAGE_SERVER permission holders. Mirrors
   *  adminPeopleApi.update but the route is /people/:userId, not
   *  /admin/people/:id, so it's reachable by non-isAdmin server admins. */
  update: (
    userId: string,
    data: Partial<Omit<PeopleRow, 'id' | 'accountStatus' | 'inviteSentAt' | 'lastSeenAt' | 'orgChartEmpId'>>,
  ) => api.patch<PeopleRow>(`/people/${userId}`, data),

  /** Single-employee read for the shared edit modal's birthday prefill.
   *  Gated server-side on canManagePeople (same as update), so it works for
   *  global admins and per-server admins alike. Birthday is absent from the
   *  list payloads, so the modal fetches it here on open. */
  getOne: (userId: string) => api.get<PeopleRow>(`/people/${userId}`),

  /** Bulk variants — same shape as adminPeopleApi but gated on
   *  canManagePeople. The Server-Admin Members tab uses these so
   *  server admins (no global isAdmin) can re-invite / block /
   *  re-enable / patch their members in batches. */
  bulkInvite: (userIds: string[]) =>
    api.post<{
      invited: number
      skipped: number
      results: { id: string; status: 'invited' | 'skipped'; reason?: string }[]
    }>('/people/bulk-invite', { userIds }),
  bulkDisable: (userIds: string[]) =>
    api.post<{ disabled: number }>('/people/bulk-disable', { userIds }),
  bulkEnable: (userIds: string[]) =>
    api.post<{ enabled: number; restoredToActive: number; restoredToDraft: number }>(
      '/people/bulk-enable',
      { userIds },
    ),
  /** Bulk soft-delete members (Server-Admin Members tab). Sets deletedAt,
   *  frees the email, force-logs-out. Skips self + global admins + already
   *  deleted (returned in `skipped`). Gated server-side on canManagePeople. */
  bulkDelete: (userIds: string[]) =>
    api.post<{ deleted: number; skipped: number }>('/people/bulk-delete', { userIds }),
  bulkUpdate: (
    userIds: string[],
    patch: Partial<Pick<PeopleRow, 'position' | 'cellPhone' | 'locationCountry' | 'hireDate'>>,
  ) => api.post<{ updated: number; userIds: string[] }>('/people/bulk-update', { userIds, patch }),
  sendInvite: (userId: string) =>
    api.post<{ id: string; accountStatus: PeopleAccountStatus; inviteSentAt: string; emailSent: boolean }>(
      `/people/${userId}/invite`,
    ),
  /** Admin reset of an employee's login password. Gated server-side
   *  on canManagePeople. If the target was DRAFT or INVITED, the
   *  server also promotes them to ACTIVE so they can immediately
   *  sign in with the new credential. */
  setPassword: (userId: string, password: string) =>
    api.post<{ ok: true; promoted: boolean }>(
      `/people/${userId}/set-password`,
      { password },
    ),
}

export const adminPeopleApi = {
  list: (params: { status?: PeopleAccountStatus | 'all'; q?: string; page?: number; pageSize?: number }) =>
    api.get<PeopleListResponse>('/admin/people', { params }),
  update: (id: string, data: Partial<Omit<PeopleRow, 'id' | 'accountStatus' | 'inviteSentAt' | 'lastSeenAt' | 'orgChartEmpId'>>) =>
    api.patch<PeopleRow>(`/admin/people/${id}`, data),
  invite: (id: string) =>
    api.post<{ id: string; accountStatus: PeopleAccountStatus; inviteSentAt: string; emailSent: boolean }>(
      `/admin/people/${id}/invite`,
    ),
  inviteBulk: (userIds: string[]) =>
    api.post<{
      invited: number
      skipped: number
      results: { id: string; status: 'invited' | 'skipped'; reason?: string }[]
    }>('/admin/people/invite-bulk', { userIds }),
  bulkUpdate: (
    userIds: string[],
    patch: Partial<Pick<PeopleRow, 'position' | 'departmentName' | 'cellPhone' | 'locationCountry' | 'hireDate'>>,
  ) => api.post<{ updated: number; userIds: string[] }>('/admin/people/bulk-update', { userIds, patch }),
  disableBulk: (userIds: string[]) =>
    api.post<{ disabled: number }>('/admin/people/disable-bulk', { userIds }),
  /** Phase C: SOFT delete — sets deletedAt (hidden from lists, login + socket
   *  blocked, fully recoverable). The DELETE /admin/users/:id route does the
   *  soft update + force-logout. */
  deleteUser: (id: string) =>
    api.delete<{ success: boolean }>(`/admin/users/${id}`),
}

export const bookingApi = {
  list: () => api.get<BookingLink[]>('/booking-links'),
  get: (id: string) => api.get<BookingLink>(`/booking-links/${id}`),
  create: (data: BookingLinkInput) => api.post<BookingLink>('/booking-links', data),
  update: (id: string, data: Partial<BookingLinkInput>) =>
    api.patch<BookingLink>(`/booking-links/${id}`, data),
  delete: (id: string) => api.delete(`/booking-links/${id}`),
  appointments: (id: string) =>
    api.get<BookingAppointment[]>(`/booking-links/${id}/appointments`),
}

export interface PublicBookingLink {
  slug: string
  title: string
  description: string | null
  durationMinutes: number
  /** IANA timezone the link was configured in. */
  timezone: string
  startsAt: string | null
  endsAt: string | null
  owner: {
    username: string
    firstName: string | null
    lastName: string | null
    avatarUrl: string | null
  }
}

export interface PublicSlot { startsAt: string; endsAt: string }

export interface PublicBookingDetails {
  id: string
  status: string
  startsAt: string
  endsAt: string
  customerName: string
  customerEmail: string
  notes: string | null
  link: {
    title: string
    timezone: string
    owner: { username: string; firstName: string | null; lastName: string | null }
  }
}

export const publicBookingApi = {
  // Use the bare api instance — the request interceptor will skip
  // attaching the JWT when there isn't one in localStorage, which
  // is the normal case for an external visitor on /book/<slug>.
  get: (slug: string) => api.get<PublicBookingLink>(`/public/booking/${slug}`),
  slots: (slug: string, fromIso: string, toIso: string) =>
    api.get<{ timezone: string; slots: PublicSlot[] }>(`/public/booking/${slug}/slots`, {
      params: { from: fromIso, to: toIso },
    }),
  book: (slug: string, body: { startsAt: string; customerName: string; customerEmail: string; notes?: string | null }) =>
    api.post<{ id: string; startsAt: string; endsAt: string; customerName: string; customerEmail: string; cancelToken: string }>(
      `/public/booking/${slug}/book`,
      body,
    ),
  // OSPROD-BOOK followup: customer self-cancel by token (sent in
  // the confirmation email). No auth.
  getCancelDetails: (token: string) => api.get<PublicBookingDetails>(`/public/booking/cancel/${token}`),
  cancel: (token: string) => api.post<{ ok: boolean; alreadyCancelled?: boolean }>(`/public/booking/cancel/${token}`),
}

export const dmApi = {
  list: () => api.get('/dm-channels'),
  create: (userIds: string[]) => api.post('/dm-channels', { userIds }),
  get: (id: string) => api.get(`/dm-channels/${id}`),
  messages: (id: string, params?: { beforeId?: string; limit?: number }) =>
    api.get(`/dm-channels/${id}/messages`, { params }),
  send: (id: string, data: {
    content: string
    replyToId?: string
    audioUrl?: string
    audioDuration?: number
    videoUrl?: string
    videoDuration?: number
    photoUrl?: string
    photoWidth?: number
    photoHeight?: number
    stickerId?: string
    gifUrl?: string
    gifWidth?: number
    gifHeight?: number
    attachments?: Array<{ fileName: string; fileUrl: string; fileSize: number; mimeType: string }>
    localId?: string
    forwardedFromName?: string
    forwardedFromMessageId?: string
  }) => api.post(`/dm-channels/${id}/messages`, data),
  createGroup: (data: { name: string; description?: string; userIds: string[] }) =>
    api.post('/dm-channels/group', data),
  // OSPROD-131: optional `userIds` — DM-channel modal pre-invites the
  // picked users as MEMBER rows on creation (same shape as createGroup).
  // Empty / omitted keeps the original single-owner broadcast behaviour.
  createChannel: (data: { name: string; description?: string; userIds?: string[] }) =>
    api.post('/dm-channels/channel', data),
  update: (id: string, data: { name?: string; description?: string; avatarUrl?: string | null }) =>
    api.put(`/dm-channels/${id}`, data),
  addMembers: (id: string, userIds: string[]) =>
    api.post(`/dm-channels/${id}/members`, { userIds }),
  removeMember: (id: string, userId: string) =>
    api.delete(`/dm-channels/${id}/members/${userId}`),
  changeRole: (id: string, userId: string, role: 'CREATOR' | 'ADMIN' | 'MEMBER') =>
    api.put(`/dm-channels/${id}/members/${userId}/role`, { role }),
  leave: (id: string) =>
    api.post(`/dm-channels/${id}/leave`),
  delete: (id: string) => api.delete(`/dm-channels/${id}`),
  markAsRead: (id: string) => api.post(`/dm-channels/${id}/read`),
  readMessage: (messageId: string) =>
    api.post(`/dm-channels/messages/${messageId}/read`),
  messageReaders: (messageId: string) =>
    api.get<{ userId: string; readAt: string; user: { id: string; username: string; avatarUrl: string | null } | null }[]>(
      `/dm-channels/messages/${messageId}/readers`
    ),
  searchMessages: (id: string, q: string) =>
    api.get(`/dm-channels/${id}/search`, { params: { q } }),
  media: (id: string, kind: 'all' | 'photo' | 'video' | 'audio' | 'file' | 'link' = 'all', limit = 60) =>
    api.get(`/dm-channels/${id}/media`, { params: { kind, limit } }),
  // Telegram-style threads. List returns { root, messages[] }; send
  // returns { message, threadReplyCount }. Delete nukes the entire
  // thread (root author or DM admin only).
  threadMessages: (dmId: string, rootId: string, params?: { beforeId?: string; limit?: number }) =>
    api.get(`/dm-channels/${dmId}/threads/${rootId}/messages`, { params }),
  threadSend: (dmId: string, rootId: string, data: {
    content: string
    audioUrl?: string
    audioDuration?: number
    videoUrl?: string
    videoDuration?: number
    photoUrl?: string
    photoWidth?: number
    photoHeight?: number
    stickerId?: string
    gifUrl?: string
    gifWidth?: number
    gifHeight?: number
    attachments?: Array<{ fileName: string; fileUrl: string; fileSize: number; mimeType: string }>
  }) => api.post(`/dm-channels/${dmId}/threads/${rootId}/messages`, data),
  threadDelete: (dmId: string, rootId: string) =>
    api.delete(`/dm-channels/${dmId}/threads/${rootId}`),
}

export const notificationApi = {
  list: () => api.get('/notifications'),
  markRead: (id: string) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/read-all'),
  delete: (id: string) => api.delete(`/notifications/${id}`),
  deleteAll: () => api.delete('/notifications'),
  // OSPROD-191: personal unread task-events counter for the Tasks rail badge.
  tasksUnreadCount: () => api.get<{ count: number }>('/notifications/tasks/unread-count'),
  tasksMarkRead: () => api.patch<{ success: true; count: number }>('/notifications/tasks/read-all'),
  // OSPROD-191 (QA round 2): per-board breakdown + per-board mark-read — the
  // badge drains board by board as the user actually opens each board.
  tasksUnreadByBoard: () => api.get<{ byBoard: Record<string, number> }>('/notifications/tasks/unread-by-board'),
  tasksMarkBoardRead: (boardId: string) =>
    api.patch<{ success: true; count: number }>('/notifications/tasks/read-board', { boardId }),
}

export const voiceSessionApi = {
  serverStats: (serverId: string, params?: { from?: string; to?: string }) =>
    api.get(`/voice-sessions/server/${serverId}/stats`, { params }),
  serverSessions: (serverId: string, params?: { from?: string; to?: string; userId?: string; channelId?: string }) =>
    api.get(`/voice-sessions/server/${serverId}`, { params }),
  userSessions: (userId: string, params?: { from?: string; to?: string }) =>
    api.get(`/voice-sessions/user/${userId}`, { params }),
  userStats: (userId: string) =>
    api.get(`/voice-sessions/user/${userId}/stats`),
  channelSessions: (channelId: string, params?: { from?: string; to?: string }) =>
    api.get(`/voice-sessions/channel/${channelId}`, { params }),
  transcriptSessions: (channelId: string) =>
    api.get(`/voice-sessions/transcripts/${channelId}`),
}

export const historyApi = {
  list: (channelId: string, params?: { from?: string; to?: string; limit?: number }) =>
    api.get<HistorySessionListItem[]>(`/voice-sessions/history/${channelId}`, { params }),
  session: (sessionId: string, params?: { tagId?: string; pinned?: boolean; q?: string }) =>
    api.get<HistorySessionDetail>(`/voice-sessions/history/session/${sessionId}`, { params }),
}

export const callHistoryApi = {
  mine: (params?: { before?: string; limit?: number }) =>
    api.get<{ items: CallHistoryItem[]; nextBefore: string | null }>(`/call-history/mine`, { params }),
  // Detail for a non-voice call (dm | pstn) in the HistorySessionDetail shape.
  // Voice items fetch detail via historyApi.session(sessionId) instead.
  item: (source: string, id: string) =>
    api.get<HistorySessionDetail>(`/call-history/item/${source}/${id}`),
}

export const inviteApi = {
  get: (code: string) => api.get(`/invites/${code}`),
  join: (code: string) => api.post(`/invites/${code}/join`),
}

export const uploadApi = {
  /**
   * `onProgress` reports byte-level send progress as a 0–100 integer so
   * upload overlays can show a real bar instead of a spinner. The 100%
   * tick fires when the body finishes uploading, BEFORE the server's
   * response — caller still needs to await this promise.
   */
  upload: (files: File[], opts?: { onProgress?: (percent: number) => void; signal?: AbortSignal }) => {
    const formData = new FormData()
    files.forEach((f) => formData.append('files', f))
    // Don't set Content-Type manually — axios/browser will generate the
    // `multipart/form-data; boundary=...` header automatically. Setting
    // it without the boundary string makes the server unable to parse
    // the body and the request hangs in pending forever.
    return api.post<{ files: Array<{ fileName: string; fileUrl: string; fileSize: number; mimeType: string }> }>(
      '/upload',
      formData,
      {
        signal: opts?.signal,
        onUploadProgress: opts?.onProgress
          ? (e) => {
              const total = e.total ?? 0
              if (total > 0) opts.onProgress!(Math.min(100, Math.round((e.loaded / total) * 100)))
            }
          : undefined,
      },
    )
  },
}

export interface SystemSoundEvent {
  eventKey: string
  mode: 'builtin' | 'file' | 'off'
  fileUrl: string | null
  volume: number
  presetKey: string | null
}
export interface SystemSoundPreset {
  key: string
  label: string
  category: string
  fileUrl: string
}
export const systemSoundsApi = {
  list: () => api.get<{ events: SystemSoundEvent[]; presets: SystemSoundPreset[] }>('/system-sounds'),
  set: (
    eventKey: string,
    body: { mode: 'builtin' | 'file' | 'off'; fileUrl?: string | null; presetKey?: string | null; volume?: number },
  ) => api.put<SystemSoundEvent>(`/system-sounds/${encodeURIComponent(eventKey)}`, body),
  reset: (eventKey: string) => api.delete(`/system-sounds/${encodeURIComponent(eventKey)}`),
}

export interface SoundEffect {
  id: string
  name: string
  fileUrl: string
  mimeType: string
  fileSize: number
  category: string | null
  emoji: string | null
  uploadedById: string | null
  createdAt: string
}

export const soundsApi = {
  list: () => api.get<SoundEffect[]>('/sounds'),
  upload: (file: File, opts?: { name?: string; category?: string; emoji?: string }) => {
    const fd = new FormData()
    fd.append('file', file)
    if (opts?.name) fd.append('name', opts.name)
    if (opts?.category) fd.append('category', opts.category)
    if (opts?.emoji) fd.append('emoji', opts.emoji)
    return api.post<SoundEffect>('/sounds', fd)
  },
  remove: (id: string) => api.delete(`/sounds/${id}`),
}

export const timeApi = {
  list: (params?: { startDate?: string; endDate?: string; category?: string; projectId?: string; taskId?: string }) =>
    api.get('/time', { params }),
  current: () => api.get('/time/current'),
  start: (data?: { category?: string; description?: string; project?: string; projectId?: string; taskId?: string; jiraIssueId?: string; source?: string }) =>
    api.post('/time/start', data),
  stop: () => api.post('/time/stop'),
  pause: () => api.post('/time/pause'),
  resume: (data?: { category?: string; description?: string; project?: string; projectId?: string; taskId?: string; source?: string }) =>
    api.post('/time/resume', data),
  setCategory: (category: string) => api.post('/time/category', { category }),
  heartbeat: (data: {
    keystrokes: number; mouseClicks: number; mouseMoves: number; scrollEvents: number;
    activeSeconds: number; idleSeconds: number; activeWindow?: string; activeUrl?: string;
    source?: 'native' | 'native-web' | 'web' | string;
    inCall?: boolean;
    taskAssignmentId?: string;
    timestamp?: number;
  }) => api.post('/time/heartbeat', data),
  browsingEvent: (data: { url: string; title?: string; durationMs?: number; source?: string; domain?: string }) =>
    api.post('/time/browsing-event', data),
  create: (data: { startTime: string; endTime?: string; duration?: number; category?: string; description?: string; project?: string; projectId?: string; taskId?: string; jiraIssueId?: string }) =>
    api.post('/time', data),
  update: (id: string, data: Partial<{ description: string; category: string; project: string; projectId: string; taskId: string; jiraIssueId: string }>) =>
    api.put(`/time/${id}`, data),
  delete: (id: string) => api.delete(`/time/${id}`),
  dailyReport: (date?: string) => api.get('/time/report/daily', { params: { date } }),
}

export const projectApi = {
  list: () => api.get('/projects'),
  listAll: () => api.get('/projects/all'),
  create: (data: { name: string; description?: string; color?: string; billable?: boolean; defaultRate?: number; currency?: string }) =>
    api.post('/projects', data),
  update: (id: string, data: Partial<{ name: string; description: string; color: string; billable: boolean; defaultRate: number; currency: string; archived: boolean }>) =>
    api.put(`/projects/${id}`, data),
  archive: (id: string) => api.delete(`/projects/${id}`),
  listTasks: (projectId: string) => api.get(`/projects/${projectId}/tasks`),
  createTask: (projectId: string, data: { title: string; description?: string; status?: string; estimateSec?: number; jiraIssueId?: string; assigneeIds?: string[] }) =>
    api.post(`/projects/${projectId}/tasks`, data),
  updateTask: (taskId: string, data: Partial<{ title: string; description: string; status: string; estimateSec: number; jiraIssueId: string; assigneeIds: string[] }>) =>
    api.put(`/projects/tasks/${taskId}`, data),
  archiveTask: (taskId: string) => api.delete(`/projects/tasks/${taskId}`),
}

// employeesApi removed in v0.1.15A — superseded by /v1/tracker/* endpoints
// in the Tracker module. Use trackerApi (modules/tracker/services/api.ts).

export const aiApi = {
  listConversations: (filter: 'active' | 'archived' = 'active') => api.get(`/ai/conversations?filter=${filter}`),
  sharedConversations: () => api.get('/ai/conversations/shared'),
  createConversation: (data?: { title?: string; model?: string }) => api.post('/ai/conversations', data),
  getConversation: (id: string) => api.get(`/ai/conversations/${id}`),
  updateConversation: (id: string, data: { model?: string; title?: string; description?: string; archived?: boolean }) => api.patch(`/ai/conversations/${id}`, data),
  regenerateMeta: (id: string) => api.post(`/ai/conversations/${id}/regenerate-meta`),
  getModels: () => api.get('/ai/models'),
  deleteConversation: (id: string) => api.delete(`/ai/conversations/${id}`),
  sendMessage: (id: string, content: string) => api.post(`/ai/conversations/${id}/messages`, { content }),
  listShares: (id: string) => api.get(`/ai/conversations/${id}/shares`),
  share: (id: string, data: { granteeType: 'USER' | 'DEPARTMENT'; granteeUserId?: string; granteeDepartmentId?: string }) => api.post(`/ai/conversations/${id}/shares`, data),
  unshare: (id: string, shareId: string) => api.delete(`/ai/conversations/${id}/shares/${shareId}`),
  searchShareUsers: (q: string) => api.get(`/ai/share/users?q=${encodeURIComponent(q)}`),
  listDepartments: () => api.get('/ai/share/departments'),
}

export const aiAttachmentsApi = {
  upload: (file: File, conversationId: string) => {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('conversationId', conversationId)
    return api.post('/ai/attachments', fd)
  },
  transcribe: (blob: Blob) => {
    const fd = new FormData()
    fd.append('audio', blob, 'rec.webm')
    return api.post('/ai/transcribe', fd)
  },
  rawUrl: (id: string) => {
    const t = localStorage.getItem('token') || localStorage.getItem('guestToken') || ''
    const base = (api.defaults.baseURL || '').replace(/\/+$/, '')
    return `${base}/ai/attachments/${id}/raw?token=${encodeURIComponent(t)}`
  },
}

export const dashboardApi = {
  today: () => api.get('/dashboard/today').then((r) => r.data),
  team: () => api.get('/dashboard/team').then((r) => r.data),
  myDay: () => api.get('/dashboard/myday').then((r) => r.data),
  efficiency: () => api.get('/dashboard/efficiency').then((r) => r.data),
  home: (force?: boolean) => api.get(`/dashboard/home${force ? '?refresh=1' : ''}`).then((r) => r.data),
}

export const deptDashboardApi = {
  departments: () => api.get('/dashboard/departments'),
  deptDay: (deptId: string, window: 'today' | 'week' | 'month' = 'week') =>
    api.get(`/dashboard/dept/${encodeURIComponent(deptId)}?window=${window}`),
}

export type AdminUserSort = 'name' | 'joined' | 'lastSeen'
export type AdminUserStatus = 'all' | 'DRAFT' | 'INVITED' | 'ACTIVE' | 'DISABLED'

export interface AdminUserRow {
  id: string
  email: string
  username: string
  firstName: string | null
  lastName: string | null
  position: string | null
  avatarUrl: string | null
  status: string
  role: string
  isAdmin: boolean
  accountStatus: 'DRAFT' | 'INVITED' | 'ACTIVE' | 'DISABLED'
  aboutMe: string | null
  lastSeenAt: string | null
  createdAt: string
  serverMembers: {
    server: { id: string; name: string }
    department: { id: string; name: string } | null
    roles: { role: { id: string; name: string; color: string } }[]
  }[]
}

export interface AdminUsersPageResponse {
  users: AdminUserRow[]
  total: number
  page: number
  pageSize: number
  statusCounts: Partial<Record<'DRAFT' | 'INVITED' | 'ACTIVE' | 'DISABLED', number>>
}

export const adminApi = {
  getStats: () => api.get('/admin/stats'),
  getDashboard: () => api.get('/admin/dashboard'),
  getUsers: () => api.get('/admin/users'),
  usersPage: (params: {
    q?: string
    status?: AdminUserStatus
    admin?: 'all' | 'admins'
    serverId?: string
    sort?: AdminUserSort
    page?: number
    pageSize?: number
  }) => api.get<AdminUsersPageResponse>('/admin/users.page', { params }),
  updateRole: (id: string, role: string) => api.put(`/admin/users/${id}/role`, { role }),
  updateUser: (id: string, data: {
    username?: string
    email?: string
    firstName?: string
    lastName?: string | null
    position?: string | null
    role?: string
    isAdmin?: boolean
    status?: 'ONLINE' | 'IDLE' | 'DO_NOT_DISTURB' | 'INVISIBLE' | 'OFFLINE'
    avatarUrl?: string | null
    aboutMe?: string | null
  }) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id: string) => api.delete(`/admin/users/${id}`),
  getTimeEntries: (params?: { date?: string; userId?: string }) => api.get('/admin/time-entries', { params }),
  getInvites: () => api.get('/admin/invites'),
  createInvite: (data: { email: string; serverId: string; expiresAt?: string | null }) => api.post('/admin/invites', data),
  deleteInvite: (id: string) => api.delete(`/admin/invites/${id}`),
  getServers: () => api.get('/admin/servers'),
  getServerDetails: (id: string) => api.get(`/admin/servers/${id}`),
  createServer: (data: { name: string; iconUrl?: string }) => api.post('/admin/servers', data),
  updateServer: (id: string, data: { name?: string; ownerId?: string }) => api.patch(`/admin/servers/${id}`, data),
  deleteServer: (id: string) => api.delete(`/admin/servers/${id}`),
  // Per-server module subscriptions. The endpoint lives on the
  // @os/spaces router (mounted at /api/servers) and is gated by
  // global requireAdmin. PATCH is a merge — only changed keys are
  // sent so concurrent edits don't clobber each other.
  getServerModules: (id: string) =>
    api.get<{
      enabledModules: Record<string, boolean>
      moduleAccess: Record<string, ModuleAccessScope>
    }>(`/servers/${id}/modules`),
  updateServerModules: (id: string, enabledModules: Partial<Record<string, boolean>>) =>
    api.patch<{ enabledModules: Record<string, boolean> }>(`/servers/${id}/modules`, { enabledModules }),
  // Per-module visibility scope. `public` = every member sees the
  // module; `restricted` = only the listed departments / users do.
  // Emits server:moduleAccess:updated to refresh every client's rail.
  updateModuleAccess: (serverId: string, key: string, scope: ModuleAccessScope) =>
    api.patch(`/servers/${serverId}/modules/${key}/access`, scope),
}

export interface ModuleAccessScope {
  visibility: 'public' | 'restricted' | 'admins'
  departmentIds: string[]
  userIds: string[]
}

export interface ModuleAdoptionServer {
  id: string
  name: string
  enabledModules: Record<string, boolean> | null
}
export const adminModulesApi = {
  adoption: () => api.get<{ servers: ModuleAdoptionServer[] }>('/admin/modules/adoption'),
}

export const wallpaperApi = {
  list: () => api.get('/wallpapers'),
  random: () => api.get('/wallpapers/random'),
  create: (data: { url: string; name: string; thumbUrl?: string; category?: string }) => api.post('/wallpapers', data),
  delete: (id: string) => api.delete(`/wallpapers/${id}`),
}

export const outerCallApi = {
  create: (callType: 'audio' | 'video' = 'audio') => api.post('/outer-calls', { callType }),
  get: (code: string) => api.get(`/outer-calls/${code}`),
  join: (code: string, displayName: string) => api.post(`/outer-calls/${code}/join`, { displayName }),
}

export interface StickerDTO {
  id: string
  packId: string
  emoji: string | null
  fileUrl: string
  fileFormat: 'webp' | 'tgs' | 'webm'
  width: number
  height: number
  position: number
}

export interface StickerPackDTO {
  id: string
  tgName: string
  title: string
  format: 'webp' | 'tgs' | 'webm' | 'mixed'
  scope: 'USER' | 'SERVER'
  serverId: string | null
  installedById: string | null
  stickers: StickerDTO[]
}

export const stickerApi = {
  packs: () => api.get<StickerPackDTO[]>('/stickers/packs'),
  install: (url: string, scope: 'USER' | 'SERVER' = 'USER', serverId?: string) =>
    api.post<StickerPackDTO>('/stickers/install', { url, scope, serverId }),
  uninstall: (packId: string) => api.delete(`/stickers/packs/${packId}`),
}

export interface GiphyItemDTO {
  id: string
  title: string
  previewUrl: string
  previewWidth: number
  previewHeight: number
  url: string
  width: number
  height: number
}

export const giphyApi = {
  trending: () => api.get<GiphyItemDTO[]>('/giphy/trending'),
  search: (q: string) => api.get<GiphyItemDTO[]>('/giphy/search', { params: { q } }),
}

// OSPROD-MAIL-CAL-L5a: calendar surface — sources for the multi-cal
// sidebar, range-bounded events, and three mutations that proxy to
// the Python service via the OS server.

export interface CalendarSource {
  id: number
  summary: string
  timezone: string | null
  isPrimary: boolean
  color: string
}

export interface CalendarAttendee {
  email: string
  displayName: string | null
  responseStatus: 'accepted' | 'declined' | 'tentative' | 'needsAction'
  optional: boolean
  organizer: boolean
}

export type CalendarEventScope = 'this' | 'following' | 'all'

export interface CalendarEvent {
  id: number
  sourceId: number
  providerEventId: string
  recurringMasterEventId: string | null
  title: string
  status: string
  timezone: string | null
  start: string | null  // ISO datetime (timed) or YYYY-MM-DD (all-day)
  end:   string | null
  isAllDay: boolean
  location: string | null
  meetingUrl: string | null
  etag: string | null
  color: string
  /**
   * OSPROD-BOOK-FILTER: id of the BookingLink this event was created
   * by (resolved server-side via BookingAppointment.externalEventId).
   * Null for ordinary Google/CalDAV events that didn't come from a
   * public booking page. The sidebar uses this to offer per-booking-
   * page visibility toggles and to tint the tile in the booking
   * page's colour rather than the source calendar's colour.
   */
  bookingLinkId: string | null
  // L5b: extras sourced from raw_json on the server
  description: string | null
  recurrence: string | null
  attendees: CalendarAttendee[]
  creatorEmail: string | null
  organizerEmail: string | null
  selfAttendee: CalendarAttendee | null
}

export interface CalendarEventCreateBody {
  calendarSourceId: number
  start: string
  end: string
  title: string
  description?: string
  location?: string
  attendees?: Array<{ email: string; displayName?: string }>
  recurrence?: string
  meetingUrl?: string
  // OSPROD-65c (interim): attach the creator's OS Meeting Room
  // (/meet/<slug>) to the event. The os-calendar server resolves the
  // slug and drops the link into `location` until the GCal conferencing
  // add-on writes real conferenceData.
  createOsRoom?: boolean
  isAllDay?: boolean
}
export type CalendarEventPatchBody = Partial<CalendarEventCreateBody> & {
  scope?: CalendarEventScope
  occurrenceStart?: string
  rsvpStatus?: 'accepted' | 'declined' | 'tentative'
}

// L5c: search hit + sync status shapes
export interface CalendarSearchHit {
  id: number
  sourceId: number
  title: string
  start: string | null
  end: string | null
  isAllDay: boolean
  location: string | null
  color: string
}

export interface CalendarSyncStatus {
  sourceId: number
  lastSyncAt: string | null
  status: 'fresh' | 'stale' | 'error'
}

export const calendarApi = {
  getSources: () => api.get<{ sources: CalendarSource[] }>('/calendar/sources'),
  getEvents: (params: { from: string; to: string; sourceIds?: number[]; wide?: boolean }) =>
    api.get<{ events: CalendarEvent[] }>('/calendar/events', {
      params: {
        from: params.from,
        to:   params.to,
        ...(params.sourceIds && params.sourceIds.length > 0 ? { sources: params.sourceIds.join(',') } : {}),
        ...(params.wide ? { wide: '1' } : {}),
      },
    }),
  createEvent: (body: CalendarEventCreateBody) =>
    api.post<{ id: number; providerEventId: string | null }>('/calendar/events', body),
  patchEvent:  (id: number, body: CalendarEventPatchBody) =>
    api.patch<{ id: number; providerEventId: string | null }>(`/calendar/events/${id}`, body),
  deleteEvent: (id: number, body?: { scope?: CalendarEventScope; occurrenceStart?: string }) =>
    api.delete<{ ok: true }>(`/calendar/events/${id}`, body ? { data: body } : undefined),
  // L5c
  searchEvents: (q: string, opts?: { from?: string; to?: string }) =>
    api.get<{ events: CalendarSearchHit[] }>('/calendar/events/search', {
      params: { q, ...(opts?.from ? { from: opts.from } : {}), ...(opts?.to ? { to: opts.to } : {}) },
    }),
  getSyncStatus: () =>
    api.get<{ statuses: CalendarSyncStatus[] }>('/calendar/sources/sync-status'),
  // Find-a-time
  findFreeBusy: (params: { attendees: string[]; from: string; to: string; durationMin?: number; slotStepMin?: number }) =>
    api.get<{
      attendees: Array<{
        email: string
        resolved: boolean
        busy: Array<{ start: string; end: string }>
      }>
      suggestedSlots: Array<{ start: string; end: string }>
    }>('/calendar/free-busy', {
      params: {
        attendees: params.attendees.join(','),
        from: params.from,
        to: params.to,
        ...(params.durationMin ? { durationMin: params.durationMin } : {}),
        ...(params.slotStepMin ? { slotStepMin: params.slotStepMin } : {}),
      },
    }),
}

// L3+L4: Web Push + preferences

export interface NotificationPreferenceItem {
  category: string
  channel: 'email' | 'sms' | 'push' | 'inApp'
  enabled: boolean
}

export interface NotificationCategoryItem {
  key: string
  label: string
  description: string
  channels: Array<'email' | 'sms' | 'push' | 'inApp'>
}

export const notificationsApi = {
  getVapidPublicKey: () => api.get<{ publicKey: string }>('/notifications/vapid-public-key'),
  subscribePush:    (body: { endpoint: string; keys: { p256dh: string; auth: string }; userAgent?: string }) =>
    api.post<{ ok: true }>('/notifications/push/subscribe', body),
  unsubscribePush:  (endpoint: string) =>
    api.post<{ ok: true }>('/notifications/push/unsubscribe', { endpoint }),
  getCategories:    () => api.get<{ categories: NotificationCategoryItem[] }>('/notifications/categories'),
  getPreferences:   () => api.get<{ preferences: NotificationPreferenceItem[] }>('/notifications/preferences'),
  savePreferences:  (items: NotificationPreferenceItem[]) =>
    api.put<{ ok: true }>('/notifications/preferences', { items }),
}

// ---------------------------------------------------------------------------
// Birthdays (D2)
// ---------------------------------------------------------------------------

export interface UpcomingBirthday {
  userId: string
  firstName: string
  lastName: string | null
  username: string
  position: string | null
  avatarUrl: string | null
  month: number
  day: number
  departmentId: string | null
  departmentName: string | null
  daysUntil: number
}

export interface CalendarBirthday {
  userId: string
  firstName: string
  lastName: string | null
  username: string
  position: string | null
  departmentName: string | null
  avatarUrl: string | null
  day: number
}

export const birthdaysApi = {
  upcoming: (days = 30) =>
    api.get<UpcomingBirthday[]>('/birthdays/upcoming', { params: { days } }),
  calendar: (month: number) =>
    api.get<CalendarBirthday[]>('/birthdays/calendar', { params: { month } }),
}

// --- Vacations (My Day P6 — time-off requests + who's-off calendar) ---
export const vacationsApi = {
  file: (body: { startDate: string; endDate: string; type: string; reason?: string }) =>
    api.post('/vacations', body),
  mine: () => api.get('/vacations/mine'),
  pending: () => api.get('/vacations/pending'),
  calendar: (month: number, year?: number) => api.get('/vacations/calendar', { params: { month, year } }),
  upcoming: (days = 30) => api.get('/vacations/upcoming', { params: { days } }),
  approve: (id: string) => api.patch(`/vacations/${id}/approve`),
  reject: (id: string, note?: string) => api.patch(`/vacations/${id}/reject`, { note }),
  cancel: (id: string) => api.delete(`/vacations/${id}`),
}

// --- Desktop (personal Kasm remote desktop, pilot) ---
export type DesktopOs = 'linux' | 'windows'

export const getDesktopOptions = async (): Promise<{ allowed: boolean; oses: DesktopOs[] }> => {
  const { data } = await api.get<{ allowed: boolean; oses: DesktopOs[] }>('/desktop/options')
  return data
}

export const getDesktopSession = async (os: DesktopOs = 'linux'): Promise<{ url: string }> => {
  const { data } = await api.get<{ url: string }>('/desktop/session', { params: { os } })
  return data
}

// DIY Guacamole engine: returns the WS bridge url + an encrypted connection token.
export const getGuacSession = async (
  os: DesktopOs = 'linux',
): Promise<{ engine: string; wsUrl: string; token: string }> => {
  const { data } = await api.get<{ engine: string; wsUrl: string; token: string }>(
    '/desktop/guac-session',
    { params: { os } },
  )
  return data
}

// KasmVNC engine (Linux): returns the iframe url to the kasm-bridge (TLS + signed
// token → the user's container, which serves Kasm's own low-latency web client).
export const getKasmSession = async (
  os: DesktopOs = 'linux',
): Promise<{ engine: string; url: string }> => {
  const { data } = await api.get<{ engine: string; url: string }>('/desktop/kasm-session', {
    params: { os },
  })
  return data
}

// --- Admin: OS Desktops platform ---
export interface DesktopInstanceRow {
  id: string
  tenantId: string
  userEmail: string
  templateKey: string
  os: string
  backend: string
  state: string
  connHost: string | null
  connPort: number
  lastActiveAt: string | null
  createdAt: string
  userName?: string
  department?: string
  position?: string
}
export interface DesktopTemplateRow {
  key: string
  os: string
  backend: string
  protocol: string
  image: string | null
}
export interface DesktopWindowsGrantRow {
  id: string
  userEmail: string
  vmHost: string | null
  grantedBy: string | null
  createdAt: string
  userName?: string
  department?: string
  position?: string
}
export interface DesktopActivityEvent {
  ts: number; type: string; url: string; host: string; op: string; path: string; app: string; windowTitle: string; state: string
}
export interface DesktopActivityView {
  events: DesktopActivityEvent[]
  lastScreenshot: { ts: number; blobKey: string; ocrText: string } | null
}
export interface DesktopBillingInfo {
  configured: boolean
  serverId: string | null
  activatedAt: string | null
  deactivatedAt: string | null
  anchorDay: number | null
  pricing: {
    moduleMonthlyCents: number
    linuxTerminalMonthlyCents: number
    windowsTerminalMonthlyCents: number
  }
  counts: { linux: number; windows: number }
  estMonthlyCents: number
}
export interface DesktopUsage {
  instances: Record<string, number>
  grants: Record<string, number>
}
export const desktopsAdminApi = {
  instances: () => api.get<{ instances: DesktopInstanceRow[] }>('/admin/desktops/instances'),
  usage: () => api.get<DesktopUsage>('/admin/desktops/usage'),
  templates: () => api.get<{ templates: DesktopTemplateRow[] }>('/admin/desktops/templates'),
  host: () => api.get<{ agentUrl: string | null; ok: boolean; freeMemMb: number | null }>('/admin/desktops/host'),
  suspend: (id: string) => api.post(`/admin/desktops/instances/${id}/suspend`),
  destroy: (id: string) => api.delete(`/admin/desktops/instances/${id}`),
  windowsGrants: () =>
    api.get<{ grants: DesktopWindowsGrantRow[] }>('/admin/desktops/windows-grants'),
  grantWindows: (userEmail: string, vmHost: string | null) =>
    api.post<{ grant: DesktopWindowsGrantRow }>('/admin/desktops/windows-grants', { userEmail, vmHost }),
  revokeWindows: (id: string) => api.delete(`/admin/desktops/windows-grants/${id}`),
  billing: () => api.get<DesktopBillingInfo>('/admin/desktops/billing'),
  activateBilling: (active: boolean) =>
    api.post<{ activatedAt: string | null; deactivatedAt: string | null; anchorDay: number | null }>(
      '/admin/desktops/billing/activate',
      { active },
    ),
  departments: () => api.get<{ departments: DesktopDepartmentRow[] }>('/admin/desktops/departments'),
  grantDepartment: (departmentId: string) =>
    api.post<{ granted: number; totalMembers: number }>('/admin/desktops/department-grants', { departmentId }),
  activity: (userEmail: string) =>
    api.get<DesktopActivityView>('/admin/desktops/activity', { params: { userEmail } }),
}
export interface DesktopDepartmentRow {
  id: string
  name: string
  serverId: string
  memberCount: number
}

// --- Monitoring (desktop activity) admin stats ---
export interface MonEmployee {
  email: string; name: string; dept: string | null
  status: 'online' | 'idle' | 'offline'
  activeMsToday: number; currentApp: string | null; currentHost: string | null
  lastScreenshot: { ts: number; blobKey: string } | null
}
export interface MonOverview {
  kpis: { onlineNow: number; totalUsers: number; activeMs: number; screenshots: number; events: number }
  activityByDay: { date: string; activeMs: number }[]
  topApps: { app: string; focusMs: number; pct: number }[]
  employees: MonEmployee[]
}
export interface MonSpend {
  kpis: { spendCents: number; activeDesktops: number; desktopHours: number; callMinutes: number; screensStored: number }
  spendByMonth: { month: string; cents: number }[]
  spendByCategory: { category: 'desktops' | 'telephony' | 'modules'; cents: number; pct: number }[]
  workspaces: { serverId: string; name: string; ownerEmail: string | null; desktops: number; events: number; callMinutes: number; spendCents: number; balanceCents: number }[]
  totals: { desktops: number; events: number; callMinutes: number; spendCents: number }
}
export const monitoringApi = {
  overview: (serverId: string, range: string) =>
    api.get<MonOverview>(`/monitoring/server/${serverId}/overview`, { params: { range } }),
  userTab: (serverId: string, email: string, tab: string, range: string) =>
    api.get<Record<string, unknown>>(`/monitoring/server/${serverId}/user/${encodeURIComponent(email)}`, { params: { tab, range } }),
  spend: (range: string) => api.get<MonSpend>('/monitoring/admin/spend', { params: { range } }),
  screenshotBlob: (key: string) =>
    api.get(`/monitoring/screenshot`, { params: { key }, responseType: 'blob' }),
}

// Files module — Drive-like shared folder store
export interface FilesFolder {
  id: string
  name: string
  scope: 'company' | 'department' | 'personal'
  systemKind: string
  parentFolderId: string | null
  departmentId: string | null
  ownerUserId: string | null
  read: boolean
  write: boolean
}

export interface FilesObject {
  id: string
  name: string
  size: number
  mimeType: string
  folderId: string
  uploadedByUserId: string
  createdAt: string
}

export interface FilesPathItem {
  id: string
  name: string
}

export interface FilesShare {
  id: string
  objectId: string | null
  folderId: string | null
  targetType: 'user' | 'department' | 'company'
  targetUserId: string | null
  targetDepartmentId: string | null
  perm: 'view' | 'edit'
  expiresAt: string | null
  createdAt: string
}

export const filesApi = {
  tree: (serverId: string) =>
    api.get<{ folders: FilesFolder[]; sharedObjects: FilesObject[] }>('/files/tree', { params: { serverId } }),
  folder: (id: string) =>
    api.get<{ folder: FilesFolder; subfolders: FilesFolder[]; objects: FilesObject[]; path: FilesPathItem[] }>(`/files/folders/${id}`),
  createFolder: (parentFolderId: string, name: string) =>
    api.post<{ folder: FilesFolder }>('/files/folders', { parentFolderId, name }),
  presignUpload: (folderId: string, name: string, size: number, mimeType: string) =>
    api.post<{ objectId: string; uploadUrl: string }>('/files/objects/presign-upload', { folderId, name, size, mimeType }),
  finalize: (objectId: string) =>
    api.post<{ object: FilesObject }>(`/files/objects/${objectId}/finalize`),
  rename: (kind: 'folders' | 'objects', id: string, name: string) =>
    api.patch(`/files/${kind}/${id}`, { name }),
  move: (kind: 'folders' | 'objects', id: string, dest: string) =>
    api.patch(`/files/${kind}/${id}`, kind === 'folders' ? { parentFolderId: dest } : { folderId: dest }),
  remove: (kind: 'folders' | 'objects', id: string) =>
    api.delete(`/files/${kind}/${id}`),
  blobUrl: (objectId: string, inline = false) => {
    let token = ''
    try { token = localStorage.getItem('token') || '' } catch {}
    const q = [inline ? 'inline=1' : '', token ? `token=${encodeURIComponent(token)}` : ''].filter(Boolean).join('&')
    return `/api/blob/files:${objectId}${q ? `?${q}` : ''}`
  },
  shares: () => api.get<{ shares: FilesShare[] }>('/files/shares'),
  share: (body: {
    objectId?: string
    folderId?: string
    targetType: 'user' | 'department' | 'company'
    targetUserId?: string
    targetDepartmentId?: string
    perm: 'view' | 'edit'
    expiresAt?: string
  }) => api.post<{ share: FilesShare }>('/files/shares', body),
  revokeShare: (id: string) => api.delete(`/files/shares/${id}`),
}

export interface FilesUsage {
  totalObjects: number
  totalBytes: number
  folderCount: number
  byScope: { scope: string; objects: number; bytes: number }[]
  topDepartments: { departmentId: string; name: string; objects: number; bytes: number }[]
}
export const filesAdminApi = {
  usage: (serverId: string) => api.get<FilesUsage>('/files/admin/usage', { params: { serverId } }),
}

export const serviceApi = {
  teamIndex: (qs: string) => api.get(`/workspace/service/team-index?${qs}`).then((r) => r.data),
  departmentIndex: (qs: string) => api.get(`/workspace/service/department-index?${qs}`).then((r) => r.data),
  clientsIndex: (qs: string) => api.get(`/workspace/service/clients-index?${qs}`).then((r) => r.data),
  companiesIndex: (qs: string) => api.get(`/workspace/service/companies-index?${qs}`).then((r) => r.data),
  partnersIndex: (qs: string) => api.get(`/workspace/service/partners-index?${qs}`).then((r) => r.data),
  partnerCompaniesIndex: (qs: string) => api.get(`/workspace/service/partner-companies-index?${qs}`).then((r) => r.data),
  pulse: (qs: string) => api.get(`/workspace/service/emotional-pulse?${qs}`).then((r) => r.data),
  analyze: () => api.post('/workspace/service/analyze').then((r) => r.data),
  interactionStress: (qs: string) => api.get(`/workspace/service/interaction-stress?${qs}`).then((r) => r.data),
  interactionStressPair: (qs: string) => api.get(`/workspace/service/interaction-stress/pair?${qs}`).then((r) => r.data),
  interactionStressActor: (qs: string) => api.get(`/workspace/service/interaction-stress/actor?${qs}`).then((r) => r.data),
  feedbackRanking: (qs: string) => api.get(`/workspace/service/feedback-ranking?${qs}`).then((r) => r.data),
  events: (qs: string) => api.get(`/workspace/service/events?${qs}`).then((r) => r.data),
}
